import type { CanvasKit, Image as SkImage } from 'canvaskit-wasm';
import { assetsMap, type Asset, type ImageNode, type Rect } from '@flobi/artwork-core';
import type { ImageSource } from '@flobi/artwork-core/render';
import type * as Y from 'yjs';
import { ClipFrames } from './clips';
import { SketchFrames } from './sketches';
import { corsReadUrl as readableCopy, needsMediaResolution, readableMediaUrl } from '../services/mediaRead';
import { tileUrl } from '../editor/content/tileUrl';
import { cachedFetch } from '../services/mediaCache';
import { rungFor, type PictureSizes } from './pictureSizes';
export { corsReadUrl } from '../services/mediaRead';

/**
 * The pictures a document points at, decoded once and kept.
 *
 * A document holds an asset id and a URL; the bytes live in Flobi's storage. The first
 * frame that asks for a picture starts loading it and draws nothing, and the frame after
 * it arrives draws it — which is why this asks for a repaint rather than blocking.
 *
 * A picture is loaded at the size it is drawn (Tarek, 2026-11-01: "something smart like flow
 * in media handling based on zoom level - without ruining the exports"). The canvas says how
 * big each picture is on the page (`setSizes`, `pictureSizes.ts`) and how many device pixels
 * a page unit is (`setScale`: zoom × pixel ratio); a picture on Flobi's storage arrives as the
 * CDN's copy at the smallest rung that covers that (`PICTURE_RUNGS`), and a bigger one is
 * fetched when zooming in asks for more, swapped in once it is decoded — the smaller one
 * stays on screen meanwhile. It never goes back down. The painter draws a picture by its own
 * pixel size, so a copy sits exactly where the original would. Anything that reads pixels
 * for keeps asks for the originals first (`originals`); the file exports do not come through
 * here at all (they load the document's own sources in their worker, or on the server).
 */
export class DocumentImages implements ImageSource {
  private readonly decoded = new Map<string, SkImage>();
  private readonly loading = new Set<string>();
  private readonly failed = new Map<string, string>();
  /** Which copy each decoded picture is: a rung's long side, or `Infinity` for the original. */
  private readonly level = new Map<string, number>();
  /** Pictures a bigger copy is on its way for, and the rung asked for. */
  private readonly upgrading = new Map<string, Promise<void>>();
  /** Pictures decoded to pixels the CPU can read (`originals`), rather than handed to the GPU as they are. */
  private readonly raster = new Set<string>();
  /**
   * Decoded copies waiting to be shown. Each is swapped in from an animation frame, so many
   * pixels a frame (`SWAP_PIXELS_PER_FRAME`): forty pictures arriving together used to land in
   * one frame, each with a texture to upload and its tiles to draw again, mid-zoom.
   */
  private readonly ready: Arrival[] = [];
  private draining = false;
  /** Where each picture is on the page, and what the view shows: a sharper copy is fetched only for what is in view. */
  private boxes: () => ReadonlyMap<string, readonly Rect[]> | null = () => null;
  private view: Rect | null = null;
  private sizes: () => PictureSizes | null = () => null;
  private scale = 0;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** Small copies for thumbnails the canvas draws, by asset and size (`small`). */
  private readonly smalls = new Map<string, Promise<SkImage | null>>();
  private readonly smallDecoded: SkImage[] = [];
  /** The videos behind clips, driven by the page's clock. */
  private readonly clips: ClipFrames;
  /** The sketches behind sketch layers, each running in a sandbox of its own. */
  readonly sketches: SketchFrames;
  private disposed = false;

  constructor(
    private readonly ck: CanvasKit,
    private readonly root: Y.Doc,
    private readonly onLoaded: (assetId?: string) => void,
    /** A sketch has a new live frame: the canvas draws, its tiles stay as they are. */
    onSketchFrame: () => void = () => undefined,
  ) {
    this.clips = new ClipFrames(ck, onLoaded);
    this.sketches = new SketchFrames(ck, () => onLoaded(), onSketchFrame);
  }

  dispose(): void {
    this.disposed = true;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    for (const image of this.decoded.values()) image.delete();
    this.decoded.clear();
    for (const arrival of this.ready) {
      arrival.image.delete();
      arrival.shown();
    }
    this.ready.length = 0;
    for (const image of this.smallDecoded) image.delete();
    this.smallDecoded.length = 0;
    this.smalls.clear();
    this.clips.dispose();
    this.sketches.dispose();
  }

  /** Time runs (a page plays) or stands: the clips' videos follow. */
  setFlowing(flowing: boolean): void {
    this.clips.setFlowing(flowing);
    this.sketches.setFlowing(flowing);
  }

  /**
   * The picture's own size when what is decoded is its full original (`originals`), else null: a
   * copy for the screen is the size it is drawn at, and a tool must not work on that.
   */
  originalSize(assetId: string): { width: number; height: number } | null {
    const image = this.decoded.get(assetId);
    return image && this.level.get(assetId) === Infinity ? { width: image.width(), height: image.height() } : null;
  }

  /** Why a picture is not drawing, for the editor to show. */
  errorFor(assetId: string): string | null {
    return this.failed.get(assetId) ?? this.clips.errorFor(assetId);
  }

  /** Where to read how big each picture is drawn on the page (null: not known yet). */
  setSizes(sizes: () => PictureSizes | null): void {
    this.sizes = sizes;
  }

  /** Where each picture is drawn on the page, in page units (null: not known yet). */
  setBoxes(boxes: () => ReadonlyMap<string, readonly Rect[]> | null): void {
    this.boxes = boxes;
  }

  /**
   * What the canvas shows, in page units, every frame. A picture out of view is not fetched
   * sharper until it comes into view: zooming into one corner of a page of forty photographs
   * used to fetch and decode all forty originals.
   */
  setView(view: Rect): void {
    const was = this.view;
    if (was && was.x === view.x && was.y === view.y && was.width === view.width && was.height === view.height) return;
    this.view = { x: view.x, y: view.y, width: view.width, height: view.height };
    if (was) this.refreshSoon();
  }

  /** Device pixels per page unit, every frame. A change looks for pictures now drawn too coarse. */
  setScale(scale: number): void {
    if (scale === this.scale) return;
    const grew = scale > this.scale;
    this.scale = scale;
    if (grew) this.refreshSoon();
  }

  /** The page changed (a picture placed bigger, say): look again, once things settle. */
  sizesChanged(): void {
    this.refreshSoon();
  }

  /**
   * The full originals of these pictures, decoded, before something reads their pixels to
   * keep (Copy as PNG, a layer sent to an image tool). Resolves when each one is in or has
   * failed; a failed original leaves the copy that was there.
   */
  async originals(assetIds: Iterable<string>): Promise<void> {
    const waits: Promise<unknown>[] = [];
    for (const assetId of new Set(assetIds)) {
      const asset = assetsMap(this.root).get(assetId) as Asset | undefined;
      if (!asset?.src || asset.kind === 'video' || asset.kind === 'sketch') continue;
      if (!this.decoded.has(assetId) && !this.loading.has(assetId) && !this.failed.has(assetId)) {
        this.loading.add(assetId);
        waits.push(this.load(assetId, asset.src, asset.mediaAssetId ?? assetId, Infinity, true));
        continue;
      }
      waits.push(this.upgrade(assetId, Infinity, true));
    }
    await Promise.all(waits);
    // One still loading for the screen: wait for it, then make sure it is the original.
    const pending = [...new Set(assetIds)].filter((id) => this.loading.has(id));
    for (let waited = 0; pending.some((id) => this.loading.has(id)) && !this.disposed && waited < 30_000; waited += 100) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (pending.length) await Promise.all(pending.map((id) => this.upgrade(id, Infinity, true)));
  }

  /** The copy a picture needs now: a rung, or the original. */
  private wanted(assetId: string, asset: Asset): number {
    if (!sizable(asset)) return Infinity;
    const onPage = this.sizes()?.get(assetId);
    if (onPage === undefined || !(this.scale > 0)) return Infinity;
    const longest = asset.width && asset.height ? Math.max(asset.width, asset.height) : undefined;
    return rungFor(onPage * this.scale, longest);
  }

  private refreshSoon(): void {
    if (this.disposed || this.refreshTimer) return;
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.refresh();
    }, 150);
  }

  /** Every picture drawn coarser than it is now shown gets its bigger copy. */
  private refresh(): void {
    if (this.disposed) return;
    for (const [assetId, level] of this.level) {
      if (level === Infinity || this.upgrading.has(assetId) || !this.inView(assetId)) continue;
      const asset = assetsMap(this.root).get(assetId) as Asset | undefined;
      if (!asset?.src) continue;
      const wanted = this.wanted(assetId, asset);
      if (wanted > level) void this.upgrade(assetId, wanted);
    }
    this.trim();
  }

  /**
   * Lets go of decoded pictures out of view, largest first, once what is kept passes
   * `KEPT_PIXELS`. A sharper copy never went back down, so a page of photographs panned
   * across at 200% kept every original decoded at once (the 1.5 GB tabs of 1 Nov 2026). A
   * picture let go is loaded again, at the size it is then drawn, when it comes back.
   */
  private trim(): void {
    let total = 0;
    const away: { assetId: string; pixels: number }[] = [];
    for (const [assetId, level] of this.level) {
      const asset = assetsMap(this.root).get(assetId) as Asset | undefined;
      const pixels = pixelsOf(asset, level);
      total += pixels;
      if (!this.inView(assetId) && !this.upgrading.has(assetId) && !this.loading.has(assetId)) away.push({ assetId, pixels });
    }
    if (total <= KEPT_PIXELS) return;
    away.sort((a, b) => b.pixels - a.pixels);
    for (const { assetId, pixels } of away) {
      if (total <= KEPT_PIXELS * 0.75) break;
      const image = this.decoded.get(assetId);
      this.decoded.delete(assetId);
      this.level.delete(assetId);
      this.raster.delete(assetId);
      image?.delete();
      total -= pixels;
    }
  }

  /** Whether a picture is in the view, or within a quarter of it beyond its edges; true when nothing says where it is. */
  private inView(assetId: string): boolean {
    const view = this.view;
    const boxes = view ? this.boxes()?.get(assetId) : undefined;
    if (!view || !boxes) return true;
    const dx = view.width * VIEW_MARGIN;
    const dy = view.height * VIEW_MARGIN;
    return boxes.some((box) => box.x < view.x + view.width + dx && box.x + box.width > view.x - dx && box.y < view.y + view.height + dy && box.y + box.height > view.y - dy);
  }

  /**
   * Fetches the bigger copy and swaps it in; the one on screen stays until then. `raster`
   * asks for pixels the CPU can read (`originals`), which a copy for the screen is not.
   */
  private upgrade(assetId: string, rung: number, raster = false): Promise<void> {
    const level = this.level.get(assetId);
    if (level === undefined || level > rung || (level === rung && (!raster || this.raster.has(assetId)))) return Promise.resolve();
    const running = this.upgrading.get(assetId);
    if (running) return running.then(() => this.upgrade(assetId, rung, raster));
    const asset = assetsMap(this.root).get(assetId) as Asset | undefined;
    if (!asset?.src) return Promise.resolve();
    const made = (async () => {
      try {
        const image = await this.fetchDecoded(asset.src!, asset.mediaAssetId ?? assetId, rung, raster);
        if (this.disposed) { image.delete(); return; }
        await this.show(assetId, image, rung, raster);
      } catch (error) {
        console.warn(`Could not load a sharper copy of a picture: ${(error as Error).message}`);
      } finally {
        this.upgrading.delete(assetId);
      }
    })();
    this.upgrading.set(assetId, made);
    return made;
  }

  get(assetId: string, at?: number, layer?: ImageNode & { id: string }): SkImage | null {
    if (this.disposed) return null;
    if (layer?.sketch) {
      // Its cover stands in, below, until the sketch has drawn something of its own.
      const asset = assetsMap(this.root).get(assetId) as Asset | undefined;
      const drawn = asset?.sketch ? this.sketches.get(asset, layer, at) : null;
      if (drawn) return drawn;
    }
    const hit = this.decoded.get(assetId);
    if (hit) return hit;
    if (this.loading.has(assetId) || this.failed.has(assetId)) return null;

    const asset = assetsMap(this.root).get(assetId) as Asset | undefined;
    if (!asset?.src) return null;
    // A clip's frame is the one the page's clock says, not a picture decoded once.
    if (asset.kind === 'video') return this.clips.get(assetId, asset.src, at, asset.mediaAssetId ?? assetId);
    this.loading.add(assetId);
    void this.load(assetId, asset.src, asset.mediaAssetId ?? assetId, this.wanted(assetId, asset));
    return null;
  }

  /**
   * A small copy of a picture, for a thumbnail the canvas draws (the Effects tiles): the
   * storage CDN's rendition, `size` px on its long side, which the painter places exactly as
   * it places the original (it scales a picture by its own pixel size). Anything the CDN does
   * not resize (a picture kept elsewhere, a link that needs signing, an SVG), or a copy that
   * cannot be read, is answered with the full picture once it has arrived.
   */
  small(assetId: string, size: number): Promise<SkImage | null> {
    const key = `${assetId}@${size}`;
    let made = this.smalls.get(key);
    if (!made) {
      made = this.loadSmall(assetId, size);
      this.smalls.set(key, made);
    }
    return made;
  }

  private async loadSmall(assetId: string, size: number): Promise<SkImage | null> {
    const asset = assetsMap(this.root).get(assetId) as Asset | undefined;
    if (asset?.src && asset.kind !== 'video' && !needsMediaResolution(asset.src, asset.mediaAssetId)) {
      // WebP, not the CDN's `auto`: CanvasKit reads WebP (and its transparency), not AVIF.
      const url = tileUrl(asset.src, size).replace('format=auto', 'format=webp');
      if (url !== asset.src) {
        try {
          // Its own cache key (`?cors=1`): a copy an <img> cached first carries no CORS header.
          const response = await cachedFetch(readableCopy(url), { mode: 'cors' });
          if (response.ok) {
            const image = this.ck.MakeImageFromEncoded(new Uint8Array(await response.arrayBuffer()));
            if (image && !this.disposed) {
              this.smallDecoded.push(image);
              return image;
            }
            image?.delete();
          }
        } catch {
          // The full picture stands in.
        }
      }
    }
    for (let waited = 0; !this.disposed && waited < 20_000; waited += 100) {
      const full = this.get(assetId);
      if (full || this.errorFor(assetId)) return full;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return null;
  }

  private async load(assetId: string, src: string, mediaAssetId: string, rung: number, raster = false): Promise<void> {
    try {
      const image = await this.fetchDecoded(src, mediaAssetId, rung, raster);
      if (this.disposed) {
        image.delete();
        this.loading.delete(assetId);
        return;
      }
      // `loading` holds until the copy is shown, so no frame in between asks for it again.
      await this.show(assetId, image, rung, raster);
    } catch (error) {
      this.loading.delete(assetId);
      if (this.disposed) return;
      this.failed.set(assetId, (error as Error).message);
      console.warn(`Could not load a picture: ${(error as Error).message}`);
    }
  }

  /** Shows a decoded copy from an animation frame, so many pixels a frame; resolves once it is on (or dropped). */
  private show(assetId: string, image: SkImage, rung: number, raster: boolean): Promise<void> {
    return new Promise((shown) => {
      const asset = assetsMap(this.root).get(assetId) as Asset | undefined;
      this.ready.push({ assetId, image, rung, raster, pixels: pixelsOf(asset, rung), shown });
      this.drainSoon();
    });
  }

  private drainSoon(): void {
    if (this.draining) return;
    this.draining = true;
    const frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (callback: () => void) => setTimeout(callback, 16);
    frame(() => {
      this.draining = false;
      this.drain();
    });
  }

  private drain(): void {
    let pixels = 0;
    while (this.ready.length > 0) {
      const next = this.ready[0]!;
      if (pixels > 0 && pixels + next.pixels > SWAP_PIXELS_PER_FRAME) break;
      this.ready.shift();
      pixels += next.pixels;
      this.swap(next);
    }
    if (this.ready.length > 0) this.drainSoon();
  }

  private swap({ assetId, image, rung, raster, shown }: Arrival): void {
    const was = this.level.get(assetId);
    const better = was === undefined || rung > was || (rung === was && raster && !this.raster.has(assetId));
    if (this.disposed || !better) {
      // Closed, or overtaken by a sharper copy that arrived first.
      image.delete();
    } else {
      const old = this.decoded.get(assetId);
      this.decoded.set(assetId, image);
      this.level.set(assetId, rung);
      if (raster) this.raster.add(assetId);
      else this.raster.delete(assetId);
      // What was drawn with the old copy is dropped first; a picture recorded with it holds its own reference.
      this.onLoaded(assetId);
      old?.delete();
    }
    this.loading.delete(assetId);
    shown();
  }

  /**
   * One copy of a picture, decoded: the CDN's at `rung` px on its long side, or the original.
   * A copy the CDN will not make is answered with the original. Kept in the browser by where
   * it is stored, so the next open does not download it again.
   */
  private async fetchDecoded(src: string, mediaAssetId: string, rung: number, raster = false): Promise<SkImage> {
    if (rung !== Infinity) {
      // WebP, not the CDN's `auto`: CanvasKit reads WebP (and its transparency), not AVIF.
      const url = tileUrl(src, rung, 85).replace('format=auto', 'format=webp');
      try {
        const response = await cachedFetch(readableCopy(url), { mode: 'cors' });
        if (response.ok) {
          const image = await this.decode(response, raster);
          if (image) return image;
        }
      } catch {
        // The original stands in.
      }
    }
    const url = await readableMediaUrl(src, mediaAssetId);
    if (this.disposed) throw new Error('the file closed');
    const response = await cachedFetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error(`the server answered ${response.status}`);
    const image = await this.decode(response, raster);
    if (this.disposed) {
      image?.delete();
      throw new Error('the file closed');
    }
    if (!image) throw new Error('the file is not a picture this browser can read');
    return image;
  }

  /**
   * The picture's pixels. For the screen the browser decodes them off this thread
   * (`createImageBitmap`) and the GPU takes them as they are: CanvasKit's own decoder ran
   * here, 15 ms for a 3-megapixel JPEG and far longer for a photograph, and forty of them
   * arriving in one zoom froze it (2 Nov 2026). Pixels the CPU has to read (`raster`, for
   * `originals`) are still decoded by CanvasKit: a texture draws to no raster surface. A
   * picture the browser cannot read goes to CanvasKit too. Null once the file has closed.
   */
  private async decode(response: Response, raster: boolean): Promise<SkImage | null> {
    // Development only, opt-in: a bench sets `__artworkDecodeHere = true` to measure the old way.
    const here = import.meta.env.DEV && typeof window !== 'undefined' && (window as { __artworkDecodeHere?: boolean }).__artworkDecodeHere === true;
    const textured = !raster && !here && typeof createImageBitmap === 'function' && typeof response.blob === 'function' && typeof this.ck.MakeLazyImageFromTextureSource === 'function';
    const body: Blob | ArrayBuffer = textured ? await response.blob() : await response.arrayBuffer();
    if (this.disposed) return null;
    if (body instanceof Blob) {
      try {
        // Upright and unpremultiplied as CanvasKit would decode it, so the screen matches every export.
        const bitmap = await createImageBitmap(body, { imageOrientation: 'none', premultiplyAlpha: 'none', colorSpaceConversion: 'default' });
        if (this.disposed) {
          bitmap.close();
          return null;
        }
        return this.ck.MakeLazyImageFromTextureSource(bitmap, {
          width: bitmap.width,
          height: bitmap.height,
          colorType: this.ck.ColorType.RGBA_8888,
          alphaType: this.ck.AlphaType.Unpremul,
          colorSpace: this.ck.ColorSpace.SRGB,
        });
      } catch {
        // Not a picture the browser reads: CanvasKit tries.
      }
      return this.ck.MakeImageFromEncoded(new Uint8Array(await body.arrayBuffer()));
    }
    return this.ck.MakeImageFromEncoded(new Uint8Array(body));
  }
}

/** A decoded copy on its way to the screen. */
interface Arrival {
  readonly assetId: string;
  readonly image: SkImage;
  readonly rung: number;
  readonly raster: boolean;
  readonly pixels: number;
  readonly shown: () => void;
}

/** How many pixels of arriving pictures are swapped in per frame: one 3-megapixel copy, or a dozen small ones. */
const SWAP_PIXELS_PER_FRAME = 4_000_000;
/** How far beyond the view, as a share of its size, a picture still counts as in it. */
const VIEW_MARGIN = 0.25;
/** Decoded pictures kept at most, in pixels (about 640 MB of RGBA): past it, pictures out of view are let go. */
const KEPT_PIXELS = 160_000_000;

/** The pixels a copy of a picture holds, from its size and the rung; a guess when its size is unknown. */
function pixelsOf(asset: Asset | undefined, rung: number): number {
  if (!asset?.width || !asset.height) return 1_000_000;
  const longest = Math.max(asset.width, asset.height);
  const scale = rung !== Infinity && rung < longest ? rung / longest : 1;
  return asset.width * asset.height * scale * scale;
}

/** Whether the storage CDN can make smaller copies of this picture. */
function sizable(asset: Asset): boolean {
  if (!asset.src || asset.kind !== 'image' || needsMediaResolution(asset.src, asset.mediaAssetId)) return false;
  if (asset.mime && !/^image\/(png|jpe?g|webp|avif|heic|heif|tiff?|bmp)$/i.test(asset.mime)) return false;
  return tileUrl(asset.src, PROBE) !== asset.src;
}
const PROBE = 512;
